import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { meApi, type UpdateMePayload } from "@/lib/me/me-api";

export const useMeQuery = () =>
	useQuery({
		queryKey: ["me"],
		queryFn: meApi.get,
		staleTime: 30_000,
	});

export const useUpdateMe = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (payload: UpdateMePayload) => meApi.update(payload),
		onSuccess: (user) => {
			qc.setQueryData(["me"], user);
		},
	});
};

export const useChangePassword = () =>
	useMutation({
		mutationFn: (params: { currentPassword: string; newPassword: string }) =>
			meApi.changePassword(params.currentPassword, params.newPassword),
	});

export const useSessionsQuery = () =>
	useQuery({
		queryKey: ["me", "sessions"],
		queryFn: meApi.listSessions,
		refetchInterval: 30_000,
	});

export const useRevokeSession = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => meApi.revokeSession(id),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "sessions"] }),
	});
};

export const useRevokeOtherSessions = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: () => meApi.revokeOtherSessions(),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["me", "sessions"] }),
	});
};

export const useMfaMethodsQuery = () =>
	useQuery({
		queryKey: ["me", "mfa"],
		queryFn: meApi.listMfa,
	});

export const useSetupTotp = () =>
	useMutation({
		mutationFn: () => meApi.setupTotp(),
	});

export const useVerifyTotp = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (params: { setupId: string; code: string; label?: string }) =>
			meApi.verifyTotp(params.setupId, params.code, params.label),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["me", "mfa"] });
			qc.invalidateQueries({ queryKey: ["me"] });
		},
	});
};

export const useDeleteMfa = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => meApi.deleteMfa(id),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["me", "mfa"] });
			qc.invalidateQueries({ queryKey: ["me"] });
		},
	});
};

export const useRegenerateRecoveryCodes = () =>
	useMutation({
		mutationFn: () => meApi.regenerateRecoveryCodes(),
	});
